import { useState } from "react";
import { useReveal } from "../hooks/useReveal";
import { Check } from "lucide-react";

// Obfuscated email parts (assembled in browser only, after user confirms)
const PARTS = ["silas1best", "icloud.com"];

export default function Contact() {
  const [revealed, setRevealed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { ref, visible } = useReveal();

  const handleConfirm = () => {
    if (revealed || confirming) return;
    setConfirming(true);
    // Tiny artificial delay — feels like a "verify" step
    setTimeout(() => {
      setRevealed(true);
      setConfirming(false);
    }, 700);
  };

  const email = revealed ? `${PARTS[0]}@${PARTS[1]}` : null;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      ref={ref}
      className={`relative bg-white py-28 md:py-36 px-6 md:px-12 lg:px-20 reveal ${
        visible ? "in-view" : ""
      }`}
    >
      <div className="max-w-2xl mx-auto text-center">
        <div className="overline mb-6">contact</div>

        <h2 className="font-serif-display text-5xl md:text-7xl text-[#0A0A0A] leading-[0.95] mb-8 tracking-tight">
          Get in <span className="italic">touch.</span>
        </h2>

        <p className="text-base md:text-lg text-[#595959] leading-relaxed mb-12 max-w-lg mx-auto">
          For press, bookings, collaborations, or anything else — confirm you're human to reveal the address.
        </p>

        {!revealed ? (
          <div className="flex flex-col items-center gap-6">
            {/* Robot check */}
            <button
              type="button"
              onClick={handleConfirm}
              data-testid="not-a-robot-button"
              disabled={confirming}
              className={`group inline-flex items-center gap-4 border border-[#E6EAEF] bg-white px-6 py-4 transition-all duration-500 hover:border-[#6B8FAE] ${
                confirming ? "opacity-70" : ""
              }`}
            >
              <span
                className={`w-5 h-5 border flex items-center justify-center transition-all duration-500 ${
                  confirming
                    ? "border-[#6B8FAE] bg-[#6B8FAE]"
                    : "border-[#8C8C8C] group-hover:border-[#6B8FAE]"
                }`}
              >
                {confirming && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
              </span>
              <span className="font-mono-meta text-[11px] tracking-[0.28em] uppercase text-[#0A0A0A]">
                {confirming ? "verifying…" : "i'm not a robot"}
              </span>
            </button>

            <div className="font-mono-meta text-[10px] tracking-[0.28em] uppercase text-[#8C8C8C]">
              email hidden — confirm to reveal
            </div>
          </div>
        ) : (
          <div data-testid="email-revealed" className="flex flex-col items-center gap-4">
            <a
              href={`mailto:${email}`}
              data-testid="contact-email"
              className="font-serif-display italic text-3xl md:text-5xl text-[#0A0A0A] link-underline hover:text-[#6B8FAE] transition-colors break-all"
            >
              {email}
            </a>
            <div className="flex items-center gap-2 font-mono-meta text-[10px] tracking-[0.28em] uppercase text-[#6B8FAE]">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#6B8FAE] accent-pulse" />
              verified · click to send a note
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
